import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Button, Card, ErrorText, PageHeader, Spinner } from "./ui.tsx";
import { PhaseBadge } from "./runtime.tsx";

const fetchRuntime = () => unwrap(api.me.runtime.$get());
type Runtime = Awaited<ReturnType<typeof fetchRuntime>>;

/** Why the person's assistant can't serve a page right now, or null when it can (or Krut doesn't run assistants). */
function down(d: Runtime) {
  if (!d.enabled) return null;
  if (!d.runtime) return "missing" as const;
  const p = d.runtime.phase;
  return p === "running" ? null : p === "provisioning" ? ("starting" as const) : p;
}

/**
 * Pages served by the person's own assistant (chat, files, scheduled tasks). While it's stopped, starting or
 * broken they say so at once, instead of spinning until the assistant's errors run out of retries. Krut's own
 * record answers straight away; it's polled, so the page opens by itself once the assistant is up (and notices
 * an admin stopping it meanwhile).
 */
export function NeedsAssistant({ title, fullBleed, children }: { title?: string; fullBleed?: boolean; children: ReactNode }) {
  const qc = useQueryClient();
  const rt = useQuery({
    queryKey: ["my-runtime"],
    queryFn: fetchRuntime,
    refetchInterval: (q) => (q.state.data && down(q.state.data) ? 4_000 : 30_000),
  });
  const start = useMutation({
    mutationFn: () => unwrap(api.me.runtime.restart.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-runtime"] }),
  });
  const state = rt.data ? down(rt.data) : null;
  if (!state) return <>{children}</>;

  const copy = {
    stopped: {
      heading: "Your assistant is stopped",
      text: "An admin stopped it, so chats, files and scheduled tasks are paused. Nothing is lost: it all comes back once your assistant is running.",
      action: "Start my assistant",
    },
    starting: {
      heading: "Your assistant is starting",
      text: "This takes about a minute (a few minutes the very first time). This page opens by itself when it's ready.",
      action: null,
    },
    error: {
      heading: "Your assistant isn't working",
      text: "It ran into a problem while starting. Restarting it usually fixes this.",
      action: "Restart",
    },
    missing: {
      heading: "Your assistant is being set up",
      text: "This takes a minute or two the first time. This page opens by itself when it's ready.",
      action: "Set it up now",
    },
  }[state];
  const notice = (
    <Card className="mx-auto w-full max-w-md p-8 text-center">
      <PhaseBadge phase={state === "missing" ? "provisioning" : state} />
      <h2 className="mt-4 text-lg font-semibold">{copy.heading}</h2>
      <p className="mt-2 text-sm text-[var(--muted)]">{copy.text}</p>
      {state === "error" && rt.data?.runtime?.message && <p className="mt-2 font-mono text-xs text-[var(--muted)]">{rt.data.runtime.message}</p>}
      {copy.action ? (
        <Button variant="primary" className="mt-6" disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? <Spinner /> : null}
          {copy.action}
        </Button>
      ) : (
        <div className="mt-6 flex justify-center"><Spinner /></div>
      )}
      <ErrorText error={start.error} />
    </Card>
  );
  if (fullBleed) return <div className="grid h-full place-items-center p-6">{notice}</div>;
  return (
    <>
      {title && <PageHeader title={title} />}
      <div className="pt-6">{notice}</div>
    </>
  );
}
